import { useCallback, useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { useLanguage } from "@/contexts/LanguageContext";
import companyService from "@/services/company/companyService";
import { useAuth } from "@/contexts/AuthContext";

interface ConfirmAction {
  type: "archive" | "restore";
  companyId: string;
  companyName: string;
}

export function useCompanyActions() {
  const { toast } = useToast();
  const { t } = useLanguage();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const openDialog = (type: "archive" | "restore", companyId: string, companyName: string) => {
    setConfirmAction({ type, companyId, companyName });
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setConfirmAction(null);
  };

  const handleConfirm = useCallback(async () => {
    if (!confirmAction) return;

    setIsLoading(true);
    try {
      if (confirmAction.type === "archive") {
        await companyService.archive(confirmAction.companyId, user?.uid);
        toast({
          title: t("companies.toasts.archiveSuccess.title"),
          description: t("companies.toasts.archiveSuccess.description"),
        });
      } else {
        await companyService.restore(confirmAction.companyId, user?.uid);
        toast({
          title: t("companies.toasts.restoreSuccess.title"),
          description: t("companies.toasts.restoreSuccess.description"),
        });
      }
      closeDialog();
    } catch (error) {
      console.error(`Error ${confirmAction.type}ing company:`, error);
      toast({
        variant: "destructive",
        title: t(`companies.toasts.${confirmAction.type}Error.title`),
        description: t(`companies.toasts.${confirmAction.type}Error.description`),
      });
    } finally {
      setIsLoading(false);
    }
  }, [confirmAction, toast, t, user?.uid]);

  const handleArchive = useCallback(
    (companyId: string, companyName: string) => {
      openDialog("archive", companyId, companyName);
    },
    []
  );

  const handleRestore = useCallback(
    (companyId: string, companyName: string) => {
      openDialog("restore", companyId, companyName);
    },
    []
  );

  return {
    // Confirmation dialog state
    isOpen,
    confirmAction,
    isLoading,
    closeDialog,
    handleConfirm,
    // Action handlers
    handleArchive,
    handleRestore,
  };
}
